import { Box, HStack, StyleProps, Tab, TabIndicator, TabList, TabPanel, TabPanels, Tabs, Text, useToast } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { $count, $ok, $UUID } from "foundation-ts/commons";
import { TSCountry } from "foundation-ts/tscountry";
import { country, Nullable, uint, UUID } from "foundation-ts/types";
import { Resp } from "foundation-ts/tsrequest";
import { CorporationDto, UpdateCorporationBody } from "doxecure-commons/doxecureClientTypes";
import { ConsumptionsStructure, CorporationIdentifiersDictionary, UserProfile } from "doxecure-commons/doxecureTypes";

import { SearchCorporations } from "@components/admin/SearchCorporations";
import { ColumnTitle } from "@components/ColumnTitle/ColumnTitle";
import { CorporationInfos, CorporationParameters } from "@components/admin/CorporationInfos";
import { SearchUsers } from "@components/admin/SearchUsers";
import { ConsumptionsBox } from "@components/ConsumptionsBox/ConsumptionsBox";
import { AsyncConfirmModal, DefaultAsyncConfirmModalState } from "@components/Modals/AsyncConfirmModal";
import { PageShellWithThreeColumns } from "@components/PageShellWithThreeColumns/PageShellWithThreeColumns";
import { UserParameters } from "@components/UserInfos/UserInfos";
import { G1Button } from "@components/G1Button";
import { config } from "@/config/config";
import { useAuthContext } from "@hooks/useAuthContext";
import useCorporations from "@hooks/useCorporations";
import useCountries from "@hooks/useCountries";
import useCreateCorporation from "@hooks/useCreateCorporation";
import apiClient from "@services/apiClient";
import { logout } from "@services/authentication.service";
import { deactivateCorporation, exportCorporationConsumptions, getCorporation, getCorporationConsumptions, getCorporationCurrentConsumption, getCorporationUsers, updateCorporation } from "@services/corporations.service";
import { $g1Color } from "@/theme/G1Style";
import { ActionButton } from "@utils/TypesAndConstants";
import { G1Icon } from "@utils/icons";
import { defaultErrorToast, defaultInfoToast, defaultSuccessToast } from "@utils/toast";

export const CorporationsView = ({ maxWidth }: StyleProps) => {
    const { connectedUser, disconnect }  = useAuthContext();
    const { mutateAsync: createCorporation } = useCreateCorporation() ;
    const { data: corporations } = useCorporations() ;
    const { data: countries } = useCountries() ;
    const [availableCorporationIdentifiers, setAvailableCorporationIdentifiers] = useState<Map<country, CorporationIdentifiersDictionary>>(new Map()) ;
    const [workedCorporationsList, setWorkedCorporationsList] = useState<CorporationDto[]>([]) ;
    const [corporationToShow, setCorporationToShow] = useState<CorporationParameters>({ currentMaxUsers: config.admin.minCorpUsers, minUsers: config.admin.minCorpUsers }) ;
    const [corporationUsers, setCorporationUsers] = useState<UserParameters[]>([]) ;
    const [workedCorporationUsersList, setWorkedCorporationUsersList] = useState<UserParameters[]>([]) ;
    const [actionButtons, setActionButtons] = useState<ActionButton<CorporationParameters>[]>([]) ;
    const [confirmModal, setConfirmModal] = useState(DefaultAsyncConfirmModalState) ;
    const [disableFields, setDisableFields] = useState(false) ;
    const [displayReachedLimitOfUsers, setDisplayReachedLimitOfUsers] = useState<Nullable<JSX.Element>>(null);
    const [displayCorpInfos, setDisplayCorpInfos] = useState<Nullable<JSX.Element>>(null);
    const [nbAdvanced, setNbAdvanced] = useState(0) ;
    const [nbQualified, setNbQualified] = useState(0) ;
    const [consumptions, setConsumptions] = useState<Nullable<ConsumptionsStructure>>(null) ;
    const [tabIndex, setTabIndex] = useState(0);
    const [isConnectedAdminCorp, setIsConnectedAdminCorp] = useState(false);
    const [rightPaneContent, setRightPaneContent] = useState<RightPanelType | undefined>(undefined);
    const toast = useToast() ;
    const navigate = useNavigate();
    const location = useLocation() ;
    const searchParams = new URLSearchParams(location.search) ;

    const handleSearch = async (value?: Nullable<string>) => {
        setCorporationToShow({ currentMaxUsers: config.admin.minCorpUsers, minUsers: config.admin.minCorpUsers }) ;
        setRightPaneContent(undefined);
        if ($ok(corporations)) {
            setWorkedCorporationsList($ok(value) ? corporations!.filter(corporation => corporation.name.includes(value!.toUpperCase()) || corporation.corporationIdentifier.startsWith(value!.toUpperCase())) : corporations!) ;
        }
    }

    const handleView = async (corporationId: UUID, tabIndex: number = 0) => {
        try {
            const filteredCorporations = workedCorporationsList?.filter(corporation => corporation.apid === corporationId) ;
            const corporation = $ok(filteredCorporations) && (filteredCorporations!.length > 0)  ? filteredCorporations!.first() : await getCorporation(corporationId) ;
            if ($ok(corporation)) {
                setDisableFields(!corporation!.valid) ;
                await _showCorporation(corporation!);

                const currentConsumption = await getCorporationCurrentConsumption(corporation!.apid) ;
                setNbAdvanced($ok(currentConsumption) ? currentConsumption!.advanced : -1) ;
                setNbQualified($ok(currentConsumption) ? currentConsumption!.qualified : -1) ;
                setConsumptions(await getCorporationConsumptions(corporation!.apid)) ;

                if (corporation?.valid) {
                    setActionButtons([{ title: "Mettre à jour", variant: "important", onSubmit: onSubmitUpdateCorporation }, { title: "Supprimer", variant: "important", onSubmit: onSubmitDeleteCorporation }]) ;
                // } else {
                //     setActionButtons([{ title: "Réactiver", variant: "important", onSubmit: onSubmitReactivateCorporation }]) ;
                }
                setTabIndex(tabIndex) ;
                setRightPaneContent(RightPanelType.Tabs);
                if (tabIndex === 0 && searchParams.get("corporationId") !== null) { // Remove url param if exists when click on another corporation after created a new admin corporate
                    const url = new URL(window.location.href) ;
                    url.searchParams.delete("corporationId") ;
                    window.history.replaceState({}, '', url.toString()) ;
                } 
            } else {
                toast(defaultErrorToast("Impossible de récupérer les données de cette organisation")) ;
            }
        } catch (e) {
            if ((e as Error).message === Resp.Unauthorized.toString()) {
                toast(defaultErrorToast("Erreur lors de la récupération des données de l'organisation. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.")) ;
                await _forceLogout();
            } else {
                toast(defaultErrorToast((e as Error).message)) ;
            }
        }
    }

    const handleAddCorporation = () => {
        setCorporationToShow({ currentMaxUsers: config.admin.minCorpUsers, minUsers: config.admin.minCorpUsers }) ;
        setRightPaneContent(RightPanelType.Creation);
        setActionButtons([{ title: "Créer", variant: "important", onSubmit: onSubmitAddCorporation }]) ;
    }

    const onExportConsumptions = async () => {
        if ($ok(corporationToShow)) {
            try {
                await exportCorporationConsumptions(corporationToShow!.apid!, corporationToShow!.name!) ;
            } catch (e) {
                if ((e as Error).message === Resp.Unauthorized.toString()) {
                    toast(defaultErrorToast("Erreur lors de l'export des données. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.")) ;
                    await _forceLogout();
                } else {
                    toast(defaultErrorToast("Problème rencontré lors de l'export des données")) ;
                }
            }
        }
    }

    const onSearchCorporationUser = async (search?: Nullable<string>): Promise<void> => {
        if ($ok(corporationUsers)) {
            setWorkedCorporationUsersList($ok(search) ? corporationUsers.filter(user => user.email === search || user.lastName.includes(search!.toUpperCase())) : corporationUsers) ;
        }
    }

    const onSubmitAddCorporation = async (data: CorporationParameters) => {
        try {
            const newCorporationDto = await createCorporation({
                name: data.name!.toUpperCase(),
                corporationIdentifierType: data.corporationIdentifierType!,
                corporationIdentifier: data.corporationIdentifier!,
                vatNumber: data.vatNumber,
                address: data.address!,
                billingAddress: data.billingAddress,
                maxUsers: data.currentMaxUsers
            });
            if ($ok(newCorporationDto)) {
                toast(defaultInfoToast(`L'organisation '${data.name!.toUpperCase()}' a été créé. Vous pouvez désormais lui ajouter des collaborateurs.`)) ;
                setWorkedCorporationsList([...workedCorporationsList, newCorporationDto].sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()))) ;
                setRightPaneContent(undefined);
            } else {
                toast(defaultInfoToast(`Erreur lors de la création`)) ;
            }
        } catch (e) {
            if ((e as Error).message === Resp.Unauthorized.toString()) {
                toast(defaultErrorToast("Erreur lors de l'ajout d'une organisation. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.")) ;
                disconnect();
                await logout();
                navigate(config.admin.loginURL!);
            } else {
                toast(defaultErrorToast((e as Error).message === Resp.Conflict.toString() ? "Une organisation avec cet identifiant national existe déjà" : "Erreur lors de la création de l'organisation")) ;
            }
        }
    }

    const onSubmitUpdateCorporation = async (data: CorporationParameters) => {
        try {
            if (!$ok(data.currentMaxUsers)) {
                toast(defaultErrorToast(`Vous devez définir le nombre de collaborateurs pour l'organisation ${data.name}`));
                return;
            }

            const dataToUpdate: UpdateCorporationBody = {
                name: data.name,
                corporationIdentifierType: data.corporationIdentifierType,
                corporationIdentifier: data.corporationIdentifier,
                address: data.address,
                billingAddress: data.billingAddress,
            };
            if (!isConnectedAdminCorp) {
                dataToUpdate.maxUsers = data.currentMaxUsers;
            }

            const updatedCorporationDto = await updateCorporation(data.apid!, dataToUpdate);
            if ($ok(updatedCorporationDto)) {
                toast(defaultInfoToast(`Les données de l'organisation '${data.name!.toUpperCase()}' ont été mises à jour`)) ;
                setWorkedCorporationsList(prev => {
                    const index = prev.findIndex(corporation => corporation.apid === data.apid) ;
                    if (index !== -1) {
                        const toUpdateList = [...prev];
                        toUpdateList.splice(index, 1, updatedCorporationDto);
                        return toUpdateList;
                    } else {
                        return prev;
                    }
                });
                if (data.nbActiveUsers === data.currentMaxUsers) {
                    setDisplayCorpInfos(
                        <>
                            <Box display="inline-block" verticalAlign="middle" mr={2} mb={5}> 
                                <G1Icon.Warning color="red" size={24} />
                            </Box>
                            <Text display="inline-block" verticalAlign="middle" color="red" fontStyle="italic" mb={5}>La limite du nombre de collaborateurs autorisés est atteinte</Text>
                        </>
                    );
                    setDisplayReachedLimitOfUsers(
                        <>
                            <Box display="inline-block" verticalAlign="middle" mr={2} mb={5}> 
                                <G1Icon.Warning color="red" size={24} />
                            </Box>
                            <Text display="inline-block" verticalAlign="middle" color="red" fontStyle="italic" mb={5}>La limite du nombre de collaborateurs autorisés est atteinte</Text>
                        </>
                    );
                } else {
                    setDisplayCorpInfos(null);
                    setDisplayReachedLimitOfUsers(null);
                }
            } else {
                toast(defaultInfoToast(`Erreur lors de la mise à jour`)) ;
            }
        } catch (e) {
            if ((e as Error).message === Resp.Unauthorized.toString()) {
                toast(defaultErrorToast("Erreur lors de la mise à jour d'une organisation. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.")) ;
                await _forceLogout();
            } else {
                toast(defaultErrorToast((e as Error).message === Resp.Conflict.toString() ? "Une organisation avec cet identifiant national existe déjà" : "Erreur lors de la mise à jour de l'organisation")) ;
            }
        }
    }

    const onSubmitDeleteCorporation = async (data: CorporationParameters) => {
        setConfirmModal({
            title: "Confirmation",
            message: `Etes-vous sûr de vouloir supprimer l'organisation ${data.name}`,
            onAccept: async () => {
                try {
                    if (await deactivateCorporation(data.apid!)) {
                        toast(defaultSuccessToast("Cette organisation a été supprimée. Plus aucun collaborateur ni gestionnaire ne pourront s'authentifier.")) ;
                        setRightPaneContent(undefined);
                        setDisplayCorpInfos(null);
                        setDisplayReachedLimitOfUsers(null);
                        setWorkedCorporationsList(prev => {
                            const toUpdateList = [...prev];
                            return toUpdateList.filter(corp => corp.apid !== data.apid);
                        });
                    } else {
                        toast(defaultInfoToast(`Impossible de supprimer cette organisation`)) ;
                    }
                } catch (e) {
                    if ((e as Error).message === Resp.Unauthorized.toString()) {
                        toast(defaultErrorToast("Erreur lors de la suppression d'une organisation. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.")) ;
                        await _forceLogout();
                    } else {
                        toast(defaultErrorToast("Erreur lors de la suppression de cette organisation")) ;
                    }
                }
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            },
            onClose: async () => {
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            }
        });
    }

    const onCreateAdminCorporate = async () => {
        navigate(`/admin/users?view=1&corporationId=${corporationToShow?.apid}`) ;
    }

    const _showCorporation = async (corp: CorporationDto) => {
        const users = await getCorporationUsers(corp!.apid) ;
        const nbCorporationsUsers = $count(users) as uint;
        setCorporationToShow({
            apid: corp!.apid,
            name: corp!.name,
            corporationIdentifierType: corp!.corporationIdentifierType,
            corporationIdentifier: corp!.corporationIdentifier,
            address: corp!.address,
            billingAddress: corp?.billingAddress,
            currentMaxUsers: corp?.maxUsers ?? config.admin.minCorpUsers,
            minUsers: nbCorporationsUsers !== 0 ? nbCorporationsUsers : config.admin.minCorpUsers,
            nbActiveUsers: nbCorporationsUsers
        }) ;
        setCorporationUsers($ok(users) ? users! : []) ;
        setWorkedCorporationUsersList($ok(users) ? users! : []) ;
        if (!$ok(corp?.maxUsers)) {
            setDisplayCorpInfos(
                <>
                    <Box display="inline-block" verticalAlign="middle" mr={2} mb={5}> 
                        <G1Icon.Warning color="red" size={24} />
                    </Box>
                    <Text display="inline-block" verticalAlign="middle" color="red" fontStyle="italic" mb={5}>Veuillez définir la limite du nombre de collaborateurs autorisés</Text>
                </>
            );
        } else if (nbCorporationsUsers === corp?.maxUsers) {
            setDisplayCorpInfos(
                <>
                    <Box display="inline-block" verticalAlign="middle" mr={2} mb={5}> 
                        <G1Icon.Warning color="red" size={24} />
                    </Box>
                    <Text display="inline-block" verticalAlign="middle" color="red" fontStyle="italic" mb={5}>La limite du nombre de collaborateurs autorisés est atteinte</Text>
                </>
            );
            setDisplayReachedLimitOfUsers(
                <>
                    <Box display="inline-block" verticalAlign="middle" mr={2} mb={5}> 
                        <G1Icon.Warning color="red" size={24} />
                    </Box>
                    <Text display="inline-block" verticalAlign="middle" color="red" fontStyle="italic" mb={5}>La limite du nombre de collaborateurs autorisés est atteinte</Text>
                </>
            );
        } else {
            setDisplayCorpInfos(null);
            setDisplayReachedLimitOfUsers(null);
        }
    }

    const _forceLogout = async () => {
        disconnect();
        await logout();
        navigate(config.admin.loginURL!);
    }

    useEffect(() => {
        if ($ok(connectedUser)) {
            if (connectedUser?.userFlags?.profile === UserProfile.AdministratorCorporate) {
                _showCorporation(connectedUser.corporations!.first()!);
                setActionButtons([{ title: "Mettre à jour", variant: "important", onSubmit: onSubmitUpdateCorporation }]);
                setIsConnectedAdminCorp(true);
            } else  if (searchParams.get('corporationId') !== null) {
                const corporationId = $UUID(searchParams.get('corporationId'));
                if ($ok(corporationId)) {
                    handleView(corporationId!, 2);
                }
            }
        }
        setRightPaneContent(undefined) ;
    }, [connectedUser]) ;

    useEffect(() => {
        if ($ok(corporations)) {
            setWorkedCorporationsList(corporations!) ;
        }
    }, [corporations]) ;

    useEffect(() => {
        const getCorporationIdentifiers = async () => {
            const map: Map<country, CorporationIdentifiersDictionary> = new Map() ;
            for (let g1Country of countries!) {
                const country = TSCountry.country(g1Country.code)!.alpha2Code ;
                const [corporationIdentifiersDict, _status] = await apiClient.getAvailableCorporationIdentifiers(country) ;
                if ($ok(corporationIdentifiersDict)) {
                    map.set(country, corporationIdentifiersDict!) ;
                }
            }
            setAvailableCorporationIdentifiers(map) ;
        }
        if ($ok(countries)) {
            getCorporationIdentifiers() ;
        }
    }, [countries]) ;

    return (
        <>
            {isConnectedAdminCorp ? 
            <PageShellWithThreeColumns
                centralContent={
                    <>
                        <ColumnTitle color={$g1Color('global.write')}>
                            <Text>Gestion de l'organisation</Text>
                        </ColumnTitle>
                        <CorporationInfos data={corporationToShow!} maxWidth={maxWidth} disableFields={disableFields} disableLimitOfUser={true} displayInformation={displayCorpInfos} countries={countries} availableCorporationIdentifiers={availableCorporationIdentifiers} actionButtons={actionButtons} />
                    </>
                }
                rightContent={<></>}
            />
            :
            <PageShellWithThreeColumns
                centralContent={
                    <>
                        <ColumnTitle color={$g1Color('global.write')}>
                            <Text>Gestion des organisations</Text>
                        </ColumnTitle>
                        <SearchCorporations maxWidth={maxWidth} corporations={workedCorporationsList} onSearch={handleSearch} onView={handleView} />
                        <HStack justifyContent="space-around">
                            {(connectedUser?.userFlags?.profile === UserProfile.Root || connectedUser?.userFlags?.profile === UserProfile.Administrator) && <G1Button component="form" variant="important" title="Créer une organisation" marginRight="2%" fontSize="md" height="60px" onClick={handleAddCorporation} /> }
                        </HStack>
                    </>
                }
                rightContent={
                    <>
                        {rightPaneContent === RightPanelType.Creation && (
                            <CorporationInfos maxWidth={maxWidth} data={corporationToShow} availableCorporationIdentifiers={availableCorporationIdentifiers} countries={countries} actionButtons={actionButtons} onBack={() => setRightPaneContent(undefined)} />
                        )}
                        {rightPaneContent === RightPanelType.Tabs && (
                            <Tabs isFitted marginTop="10" index={tabIndex} onChange={(index) => setTabIndex(index)}>
                                <TabList color={$g1Color('global.tabs.write')} backgroundColor={$g1Color('global.tabs.bg')}>
                                    <Tab fontWeight="semibold">Informations</Tab>
                                    <Tab fontWeight="semibold">Consommations</Tab>
                                    <Tab fontWeight="semibold">Collaborateurs</Tab>
                                </TabList>
                                <TabIndicator color={$g1Color('global.tabs.indicator.write')} backgroundColor={$g1Color('global.tabs.indicator.bg')} />
                                <TabPanels>
                                    <TabPanel>
                                        <CorporationInfos data={corporationToShow!} maxWidth={maxWidth} disableFields={disableFields} countries={countries} availableCorporationIdentifiers={availableCorporationIdentifiers}
                                                          displayInformation={displayCorpInfos} actionButtons={actionButtons} onBack={() => setRightPaneContent(undefined)} />
                                    </TabPanel>
                                    <TabPanel>
                                        <ConsumptionsBox nbAdvanced={nbAdvanced} nbQualified={nbQualified} consumptions={consumptions} onExport={onExportConsumptions} />
                                    </TabPanel>
                                    <TabPanel>
                                        <SearchUsers maxWidth={maxWidth} displayRole isAdminCorporate={isConnectedAdminCorp} users={workedCorporationUsersList} displayReachedLimitOfUsers={displayReachedLimitOfUsers} onSearch={onSearchCorporationUser} />
                                        <HStack justifyContent="space-around" mt={3}>
                                            <G1Button component="form" variant="important" title="Créer un gestionnaire" marginRight="2%" fontSize="md" height="60px" disabled={disableFields || $ok(displayReachedLimitOfUsers)} onClick={e => onCreateAdminCorporate()} /> 
                                        </HStack>
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        )}
                    </>
                }
                isOpen={rightPaneContent !== undefined}
            />}
            <AsyncConfirmModal title={confirmModal.title} message={confirmModal.message} onAccept={confirmModal.onAccept} onClose={confirmModal.onClose} />
        </>
    );
} ;

enum RightPanelType {
    Creation,
    Tabs,
}